import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { TemperatureUnitProvider } from "@/hooks/useTemperatureUnit";
import { AuthProvider } from "@/hooks/useAuth";
import { PregnancyProvider } from "@/hooks/usePregnancyMode";
import { MenopauseProvider } from "@/hooks/useMenopauseMode";
import { HealthKitProvider } from "@/hooks/useHealthKit";
import { useInactivityLogout } from "@/hooks/useInactivityLogout";

const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Support = lazy(() => import("./pages/Support"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Profile = lazy(() => import("./pages/Profile"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const InactivityGuard = ({ children }: { children: React.ReactNode }) => {
  useInactivityLogout();
  return <>{children}</>;
};

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <InactivityGuard>
          <TemperatureUnitProvider>
            <PregnancyProvider>
              <MenopauseProvider>
                <HealthKitProvider>
                  <TooltipProvider>
                    <Toaster />
                    <Sonner />
                    <BrowserRouter>
                      <Suspense fallback={<div className="flex h-screen items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>}>
                        <Routes>
                          <Route path="/" element={<Index />} />
                          <Route path="/auth" element={<Auth />} />
                          <Route path="/reset-password" element={<ResetPassword />} />
                          <Route path="/profile" element={<Profile />} />
                          <Route path="/support" element={<Support />} />
                          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                    </BrowserRouter>
                  </TooltipProvider>
                </HealthKitProvider>
              </MenopauseProvider>
            </PregnancyProvider>
          </TemperatureUnitProvider>
        </InactivityGuard>
      </AuthProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
